import Daily from '@daily-co/daily-js'
import { useAskVisio } from '@gozoki/api'
import type { VisioLink } from '@gozoki/api-types'
import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { api, tuyauClient } from '~/lib/tuyau'
import { VisioContext } from '../utils/context/visio.context'
import VideoCall, { type VisioPhase } from './VideoCall'
import { getApiErrorMessage } from '~/lib/utils'

// Borne uniquement l'établissement de l'appel (requête API + connexion à la salle Daily).
// Une fois connecté, l'appel reste ouvert jusqu'au clic sur « Quitter » : la caisse peut
// mettre plus de temps à rejoindre, et elle n'envoie pas toujours de flux vidéo.
const CALL_SETUP_TIMEOUT_MS = 12_000

const VisioComponent = () => {
  const {
    shouldStartCall,
    startCall,
    kioskId,
    storeId,
    includeVideo,
    openingEventId,
    endCall,
    setCallObject,
    clearCallObject,
    isCallInProgress,
  } = useContext(VisioContext)
  const { mutate: askVisio } = useAskVisio()
  const { mutate: endVisio } = useMutation(api.visio.endVisioRooms.mutationOptions())
  const [phase, setPhase] = useState<VisioPhase>('starting')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const requestStartedAt = useRef(0)
  const requestAttempt = useRef(0)
  const activeRoomName = useRef<string | null>(null)

  // A start still in flight has no room name yet: the server closes its room on the next start.
  const releaseRoom = useCallback(() => {
    const roomName = activeRoomName.current
    if (roomName === null) return
    activeRoomName.current = null
    endVisio({ body: { roomName } })
  }, [endVisio])

  const abandonCall = useCallback(() => {
    requestAttempt.current += 1
    clearCallObject()
    releaseRoom()
  }, [clearCallObject, releaseRoom])

  useEffect(() => abandonCall, [abandonCall])

  useEffect(() => {
    if (!isCallInProgress) return
    // Unloading while the start is in flight: its room is already inserted but not yet known, so
    // close every room of the operator, as the server itself does on the next start.
    const handlePageHide = () => {
      const roomName = activeRoomName.current
      if (roomName === null && phase !== 'starting') return
      const body = roomName === null ? {} : { roomName }
      void tuyauClient.request('visio.end_visio_rooms', { body, keepalive: true })
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [isCallInProgress, phase])

  const addTimingBreadcrumb = useCallback(
    (milestone: 'clicked' | 'api-ready' | 'joined' | 'remote-video-playing') => {
      const durationMs = requestStartedAt.current
        ? Math.round(performance.now() - requestStartedAt.current)
        : 0
      Sentry.addBreadcrumb({
        category: 'visio.timing',
        data: {
          durationMs,
          kioskId,
          storeId,
        },
        level: 'info',
        message: milestone,
      })
      if (milestone !== 'clicked') {
        Sentry.metrics.distribution(`visio.${milestone}.duration`, durationMs, {
          attributes: { kioskId: String(kioskId), storeId: String(storeId) },
          unit: 'millisecond',
        })
      }
    },
    [kioskId, storeId],
  )

  const joinRoom = useCallback(
    (visio: VisioLink, attempt: number) => {
      if (attempt !== requestAttempt.current) return
      const freshCallObject = Daily.createCallObject()
      const handleUnexpectedEnd = () => {
        if (attempt !== requestAttempt.current) return
        abandonCall()
        setErrorMessage("L'appel vidéo a été interrompu.")
        setPhase('error')
      }
      freshCallObject.on('left-meeting', handleUnexpectedEnd)
      freshCallObject.on('error', handleUnexpectedEnd)
      setCallObject(freshCallObject)
      setPhase('connecting')

      void freshCallObject
        .join({
          startVideoOff: !includeVideo,
          token: visio.token,
          url: visio.url,
        })
        .then(() => {
          if (attempt !== requestAttempt.current) return
          addTimingBreadcrumb('joined')
          setPhase('live')
        })
        .catch(() => {
          if (attempt !== requestAttempt.current) return
          abandonCall()
          setErrorMessage('La connexion à la salle vidéo a échoué.')
          setPhase('error')
        })
    },
    [abandonCall, addTimingBreadcrumb, includeVideo, setCallObject],
  )

  const requestVisio = useCallback(() => {
    if (kioskId === null) return

    const attempt = ++requestAttempt.current
    clearCallObject()
    requestStartedAt.current = performance.now()
    setErrorMessage(null)
    setPhase('starting')
    addTimingBreadcrumb('clicked')
    askVisio(
      {
        kioskId,
        includeVideo,
        openingEventId: openingEventId ?? undefined,
      },
      {
        onSuccess: (visio) => {
          if (attempt !== requestAttempt.current) {
            endVisio({ body: { roomName: visio.roomName } })
            return
          }
          activeRoomName.current = visio.roomName
          addTimingBreadcrumb('api-ready')
          joinRoom(visio, attempt)
        },
        onError: (error) => {
          if (attempt !== requestAttempt.current) return
          setErrorMessage(getApiErrorMessage(error, "Impossible de démarrer l'appel."))
          setPhase('error')
        },
      },
    )
  }, [
    addTimingBreadcrumb,
    askVisio,
    clearCallObject,
    endVisio,
    includeVideo,
    joinRoom,
    kioskId,
    openingEventId,
  ])

  useEffect(() => {
    if (!shouldStartCall || kioskId === null) return
    startCall()
    requestVisio()
  }, [kioskId, requestVisio, shouldStartCall, startCall])

  useEffect(() => {
    if (!isCallInProgress || (phase !== 'starting' && phase !== 'connecting')) return
    const elapsed = performance.now() - requestStartedAt.current
    const timeout = window.setTimeout(
      () => {
        abandonCall()
        setErrorMessage("L'appel n'a pas pu être établi dans les 12 secondes.")
        setPhase('timeout')
      },
      Math.max(0, CALL_SETUP_TIMEOUT_MS - elapsed),
    )
    return () => window.clearTimeout(timeout)
  }, [abandonCall, isCallInProgress, phase])

  const handleRemoteVideoPlayable = useCallback(() => {
    addTimingBreadcrumb('remote-video-playing')
  }, [addTimingBreadcrumb])

  const handleLeave = useCallback(() => {
    abandonCall()
    endCall()
  }, [abandonCall, endCall])

  if (!shouldStartCall && !isCallInProgress) return null

  return (
    <VideoCall
      phase={phase}
      errorMessage={errorMessage}
      leaveCall={handleLeave}
      onRemoteVideoPlayable={handleRemoteVideoPlayable}
      onRetry={requestVisio}
    />
  )
}

export default VisioComponent
